// payment-center.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { PaymentFormComponent } from "./payment-form/payment-form.component";
import { PaymentService } from './payment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { AccountBalanceComponent } from "./account-balance/account-balance.component";
import { Router, RouterModule } from '@angular/router';
import { HelpDialogComponent } from './help-dialog.component';
import { AppStateService } from '../../app-state.service';
import { PaymentPdfGenerator } from './paymentPdfGenerator.service';

interface Visit {
  date: Date;
  service: string;
  amount: number;
  code: string;
}

@Component({
  selector: 'app-payment-center',
  standalone: true,
  imports: [
    PaymentFormComponent,
    CommonModule,
    AccountBalanceComponent,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    CurrencyPipe,
    DatePipe
  ],
  templateUrl: './payment-center.component.html',
  styleUrl: './payment-center.component.scss'
})
export class PaymentCenterComponent implements OnInit {
  // Services
  private paymentService = inject(PaymentService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router); // <-- Inject Router
  appStateService = inject(AppStateService);
  private paymentPdfGenerator = inject(PaymentPdfGenerator);
  
  accountAgingBalance: { Total: number; InsEst: number; PatEstBal: number } = {
    Total: 0, InsEst: 0, PatEstBal: 0
  };

  // Data properties
  clientId: string = '';
  patientId: string = '';
  recentVisits: Visit[] = [];
  loading: boolean = false;
  patientInfo: any = {};
  clinicInfo: any = {
    displayName: '',
    address: '',
    phoneNumber: ''
  };

  constructor() {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';
    this.clinicInfo = this.appStateService.getState().clinicInfo || {
      displayName: '',
      address: '',
      phoneNumber: ''
    };
  }

  ngOnInit(): void {
    this.fetchRecentVisits();
    this.fetchAccountAgingBalance();
  }

  gotoDashboard() {
    this.appStateService.setState({ appointmentData: null });
    this.router.navigate(['/dashboard', 'main']);
  }

  /**
   * Fetch the most recent visits to display in the payment summary
   */
  fetchRecentVisits(): void {
    this.loading = true;
    this.paymentService.fetchAccountHistory(this.clientId, this.patientId).subscribe({
      next: (data: any) => {
        // Process the data and keep only the 3 most recent visits
        this.recentVisits = data
          .map((item: any) => this.formatVisitData(item))
          .sort((a: Visit, b: Visit) => b.date.getTime() - a.date.getTime())
          .slice(0, 3); // Keep only the 3 most recent visits
        this.loading = false;
      },
      error: (error) => {
        this.showNotification('Unable to load recent visits.', 'error');
        this.loading = false;
      }
    });
  }

  /**
   * Format raw visit data into our Visit interface
   */
  formatVisitData(data: any): Visit {
    let dateObj = new Date();
    if (data.date) {
      const parsedDate = new Date(data.date);
      if (!isNaN(parsedDate.getTime())) {
        dateObj = parsedDate;
      }
    }
    
    return {
      date: dateObj,
      service: this.getServiceDescription(data.code),
      amount: data.transactionType === 'Charge' ? (data.charge || 0) : -(data.credit || 0),
      code: data.code || 'Unknown'
    };
  }

  /**
   * Get a user-friendly description for a service code
   */
  getServiceDescription(code: string): string {
    const serviceDescriptions: { [key: string]: string } = {
      'OV1': 'Office Visit - New Patient',
      'OV2': 'Office Visit - Established Patient',
      'LAB': 'Laboratory Services',
      'RAD': 'Radiology',
      'PROC': 'Medical Procedure',
      'PAY': 'Payment Received',
      'INS': 'Insurance Payment'
    };
    
    return serviceDescriptions[code] || `Service (${code})`;
  }

  /**
   * Show a notification message
   */
  showNotification(message: string, type: 'info' | 'error' = 'info'): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: type === 'error' ? ['error-snackbar'] : ['info-snackbar']
    });
  }

  private fetchAccountAgingBalance(): void {
    this.paymentService
      .fetchAccountAgingBalances(this.clientId, this.patientId)
      .subscribe({
        next: data => {
          this.accountAgingBalance = {
            Total:   data.Total   || 0,
            InsEst:  data.InsEst  || 0,
            PatEstBal: data.PatEstBal || 0
          };
        },
        error: () => this.showNotification('Unable to load balance summary.', 'error')
      });
  }

  /**
   * Generate PDF statement
   */
  generatePdf(): void {
    this.showNotification('Preparing your statement for download...');
    
    if (!this.recentVisits?.length) {
      return this.showNotification('No visits to include in PDF.', 'info');
    }

    try {
      // Check if necessary data is available
      if (!this.recentVisits || this.recentVisits.length === 0) {
        throw new Error('No account history data available');
      }
      
      
      // Use the StatementPdfGenerator service to generate the PDF
      this.paymentPdfGenerator.generatePaymentSummary(
        this.recentVisits,
        this.accountAgingBalance,
        this.clinicInfo
      );
      
    } catch (error) {
      this.showNotification('Error generating statement. Please try again later.', 'error');
    }
  }
}